<script setup>
import mushroom from "cem-primary-api";
import { onMounted, ref, computed, defineProps, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import toastr from "toastr";
import MethodService from "../service/MethodService";
import NOICON from "../assets/icon/none-icon.svg";

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  allowTextInput: {
    type: Boolean,
    default: false,
  },
  iconSize: {
    type: Number,
    default: 32,
  },
});

const emit = defineEmits(["update:modelValue"]);

const listIconMenu = ref([]);
const countClick = ref(0);

const isIconId = computed(() => MethodService.isIconId(props.modelValue));

const iconPreviewSrc = computed(() => {
  if (isIconId.value) {
    return MethodService.thumbIcon(props.modelValue, props.iconSize);
  }
  return null;
});

const iconTextValue = computed({
  get() {
    return isIconId.value ? "" : (props.modelValue || "");
  },
  set(val) {
    emit("update:modelValue", val);
  },
});

const getListIconMenu = async () => {
  try {
    const response = await mushroom.icon.getAllAsync();
    if (response.result) {
      response.result.forEach((item) => {
        item.srcIcon = MethodService.thumbIcon(item.file_id, props.iconSize);
      });
      listIconMenu.value = response.result;
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

const setIcon = (icon) => {
  emit("update:modelValue", icon.file_id);
  countClick.value = 0;
};

const uploadIcon = async () => {
  try {
    const fileEl = document.getElementById("uploadIconPicker");
    const nameImage = fileEl.files[0]?.name;
    const typeImage = nameImage?.slice(nameImage.indexOf(".")).toLowerCase();

    if (typeImage) {
      if (
        typeImage !== ".jpg" &&
        typeImage !== ".png" &&
        typeImage !== ".jpeg" &&
        typeImage !== ".svg" &&
        typeImage !== ".ico"
      ) {
        toastr.warning(t("t_select_format_image"));
      } else {
        try {
          const res = await mushroom.$file.uploadAsync({
            file: fileEl.files[0],
          });
          if (res.result) {
            emit("update:modelValue", res.result);
          }
        } catch (e) {
          console.error(e);
          toastr.warning(t("t_upload_failed"));
        }
      }
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

const onInputClick = (event) => {
  event.target.value = "";
};

onMounted(() => {
  getListIconMenu();
});
</script>

<template>
  <div class="icon-picker">
    <!-- Preview icon -->
    <div class="icon-picker__preview" :style="{ width: iconSize + 'px', height: iconSize + 'px' }">
      <img
        v-if="isIconId"
        :src="iconPreviewSrc"
        alt="icon"
        class="icon-picker__preview-img"
      />
      <i
        v-else-if="modelValue"
        :class="modelValue"
        class="icon-picker__preview-i"
      ></i>
      <img v-else :src="NOICON" alt="" :height="iconSize" />
    </div>

    <!-- Chọn icon dropdown -->
    <div class="icon-picker__select">
      <div
        class="icon-picker__dropdown"
        @click="countClick++"
        v-click-outside="() => { countClick = 0; }"
      >
        {{ $t("t-select-icon") }}
      </div>
      <div
        class="icon-picker__list"
        :class="{ show: countClick % 2 === 1 }"
      >
        <div v-if="listIconMenu.length > 0">
          <div
            v-for="item in listIconMenu"
            :key="item.id"
            class="icon-picker__item"
            @click="setIcon(item)"
          >
            <img :src="item.srcIcon" alt="icon" height="20" />
          </div>
        </div>
        <p v-else>{{ $t("t_not_have_icon") }}</p>
      </div>
    </div>

    <span class="icon-picker__or">{{ $t("t-or") }}</span>

    <!-- Upload icon -->
    <label for="uploadIconPicker" class="icon-picker__upload">
      <img
        src="../assets/icon/upload_white.svg"
        alt="upload"
      />
      <span>{{ $t("t-upload") }}</span>
    </label>
    <input
      type="file"
      accept=".jpg, .png, .jpeg, .svg, .ico"
      id="uploadIconPicker"
      style="display: none"
      @change="uploadIcon"
      @click="onInputClick"
    />

    <template v-if="allowTextInput">
      <span class="icon-picker__or">{{ $t("t-or") }}</span>

      <!-- Nhập class icon thủ công -->
      <el-input
        type="text"
        autocomplete="off"
        v-model="iconTextValue"
        :placeholder="$t('t-place-search-strategy-icon')"
        style="width: 250px"
      />
    </template>
  </div>
</template>

<style scoped>
.icon-picker {
  display: flex;
  align-items: center;
  gap: 12px;
}

.icon-picker__preview {
  border-radius: 3px;
  background: #2c3e50;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-picker__preview-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.icon-picker__preview-i {
  font-size: 20px;
  color: #fff;
}

.icon-picker__or {
  font-size: 13px;
  color: #909399;
  white-space: nowrap;
}

.icon-picker__select {
  position: relative;
}

.icon-picker__dropdown {
  padding: 0 16px;
  height: 32px;
  line-height: 32px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  background: #fff;
  white-space: nowrap;
  transition: border-color 0.2s;
}

.icon-picker__dropdown:hover {
  border-color: #409eff;
}

.icon-picker__list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 200;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 8px;
  margin-top: 4px;
  display: none;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.icon-picker__list.show {
  display: block;
}

.icon-picker__list > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.icon-picker__item {
  width: 100%;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.icon-picker__item:hover {
  background: #f0f2f5;
}

.icon-picker__upload {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 32px;
  background: #f7941d;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: background 0.2s;
  margin-bottom: 0;
}

.icon-picker__upload:hover {
  background: #e8850f;
}

.icon-picker__upload img {
  height: 14px;
}
</style>
